import { memo, useMemo } from 'react';

import type { MouseEvent as ReactMouseEvent } from 'react';

export interface SpectrumWaterfallAllocationSegment {
    readonly badgeLabel?: string | null;
    readonly compactLabel?: string | null;
    readonly endMHz: number;
    readonly id: string | number;
    readonly label: string;
    readonly startMHz: number;
    readonly title?: string | null;
    readonly tone?: 'default' | 'muted' | 'warning';
}

export interface SpectrumWaterfallAllocationOverlay {
    readonly emptyLabel?: string | null;
    readonly label?: string;
    readonly onSegmentClick?: (
        segment: SpectrumWaterfallAllocationSegment,
        event: ReactMouseEvent<HTMLButtonElement>,
    ) => void;
    readonly segments: readonly SpectrumWaterfallAllocationSegment[];
    readonly visualizationMode?: 'bars' | 'denseBand';
}

interface AllocationOverlayBandProps {
    readonly allocationBandHeight: number;
    readonly allocationLayoutPxPerMHz: number;
    readonly allocationOverlay: SpectrumWaterfallAllocationOverlay | null;
    readonly allocationViewport: { endMHz: number; startMHz: number } | null;
    readonly effectiveLeftPad: number;
    readonly totalMHz: number;
}

function AllocationOverlayBand({
    allocationBandHeight,
    allocationLayoutPxPerMHz,
    allocationOverlay,
    allocationViewport,
    effectiveLeftPad,
    totalMHz,
}: Readonly<AllocationOverlayBandProps>) {
    const visibleAllocationSegments = useMemo(() => {
        if (!allocationOverlay || !allocationViewport) {
            return [];
        }

        const viewportBufferMHz = Math.max(20, 160 / Math.max(1e-6, allocationLayoutPxPerMHz));
        const minVisibleMHz = Math.max(0, allocationViewport.startMHz - viewportBufferMHz);
        const maxVisibleMHz = Math.min(totalMHz, allocationViewport.endMHz + viewportBufferMHz);

        return allocationOverlay.segments.filter((segment) => {
            return segment.endMHz >= minVisibleMHz && segment.startMHz <= maxVisibleMHz;
        });
    }, [allocationLayoutPxPerMHz, allocationOverlay, allocationViewport, totalMHz]);

    if (!allocationOverlay) {
        return null;
    }

    const isDenseBandMode = allocationOverlay.visualizationMode === 'denseBand';

    return (
        <div
            style={{
                background: isDenseBandMode ? 'linear-gradient(180deg, #091622 0%, #06101b 100%)' : '#07121f',
                borderBottom: '1px solid #203247',
                height: allocationBandHeight,
                left: 0,
                pointerEvents: 'none',
                position: 'absolute',
                top: 0,
                width: '100%',
                zIndex: 6,
            }}
        >
            {allocationOverlay.segments.length === 0 && allocationOverlay.emptyLabel ? (
                <div
                    style={{
                        alignItems: 'center',
                        color: '#70869a',
                        display: 'flex',
                        fontSize: 11,
                        height: '100%',
                        left: effectiveLeftPad + 12,
                        pointerEvents: 'none',
                        position: 'absolute',
                        top: 0,
                        whiteSpace: 'nowrap',
                    }}
                >
                    {allocationOverlay.emptyLabel}
                </div>
            ) : null}

            {visibleAllocationSegments.map((segment) => {
                const viewportStartPx = allocationViewport
                    ? effectiveLeftPad + allocationViewport.startMHz * allocationLayoutPxPerMHz
                    : 0;
                const viewportEndPx = allocationViewport
                    ? effectiveLeftPad + allocationViewport.endMHz * allocationLayoutPxPerMHz
                    : Number.MAX_SAFE_INTEGER;
                const segmentLeftPx = effectiveLeftPad + segment.startMHz * allocationLayoutPxPerMHz;
                const segmentWidthPx = Math.max(1, (segment.endMHz - segment.startMHz) * allocationLayoutPxPerMHz);
                const segmentRightPx = segmentLeftPx + segmentWidthPx;
                const visibleWidthPx = Math.max(
                    0,
                    Math.min(segmentRightPx, viewportEndPx) - Math.max(segmentLeftPx, viewportStartPx),
                );
                const contentWidthPx = Math.max(0, visibleWidthPx);
                const compactLabel = segment.compactLabel ?? segment.label;
                const fullLabelEstimatePx = segment.label.length * (isDenseBandMode ? 6.1 : 6.8) + 18;
                const compactLabelEstimatePx = compactLabel.length * (isDenseBandMode ? 5.6 : 6.2) + 12;
                const useFullLabel = segment.label === compactLabel || contentWidthPx >= fullLabelEstimatePx;
                const resolvedLabel = useFullLabel ? segment.label : compactLabel;
                const labelDisplayThresholdPx = Math.max(
                    isDenseBandMode ? 26 : 22,
                    Math.min(isDenseBandMode ? 56 : 44, compactLabelEstimatePx * 0.48),
                );
                const showLabel = contentWidthPx >= labelDisplayThresholdPx;
                const showBadge =
                    Boolean(segment.badgeLabel) && !useFullLabel && contentWidthPx >= (showLabel ? 96 : 44);
                const horizontalPaddingPx =
                    showLabel || showBadge
                        ? contentWidthPx < (isDenseBandMode ? 52 : 44)
                            ? 4
                            : isDenseBandMode
                              ? 6
                              : 8
                        : 0;
                const backgroundColor =
                    segment.tone === 'warning' ? '#6e4f00' : segment.tone === 'muted' ? '#0b6270' : '#11839a';
                const borderColor =
                    segment.tone === 'warning' ? '#f6c65b' : segment.tone === 'muted' ? '#7ad7de' : '#b0f0f4';
                const denseBackground =
                    segment.tone === 'warning'
                        ? 'linear-gradient(180deg, rgba(246, 198, 91, 0.28) 0%, #6e4f00 100%)'
                        : segment.tone === 'muted'
                          ? 'linear-gradient(180deg, rgba(122, 215, 222, 0.22) 0%, #0b6270 100%)'
                          : 'linear-gradient(180deg, rgba(176, 240, 244, 0.24) 0%, #11839a 100%)';
                const hiddenLeftPx = Math.max(0, viewportStartPx - segmentLeftPx);
                const minimumTrailingSpacePx = showBadge ? 68 : 32;
                const maxContentOffsetPx = Math.max(0, segmentWidthPx - minimumTrailingSpacePx);
                const contentOffsetPx = showLabel ? Math.min(hiddenLeftPx, maxContentOffsetPx) : 0;

                return (
                    <button
                        key={String(segment.id)}
                        onClick={(event) => {
                            allocationOverlay.onSegmentClick?.(segment, event);
                        }}
                        style={{
                            alignItems: 'center',
                            background: isDenseBandMode ? denseBackground : backgroundColor,
                            border: `1px solid ${borderColor}`,
                            borderRadius: isDenseBandMode ? 4 : 8,
                            boxShadow: isDenseBandMode
                                ? 'inset 0 1px 0 rgba(255,255,255,0.12), inset 0 -1px 0 rgba(0,0,0,0.2)'
                                : 'none',
                            boxSizing: 'border-box',
                            color: '#f4fdff',
                            cursor: allocationOverlay.onSegmentClick ? 'pointer' : 'default',
                            display: 'flex',
                            gap: 6,
                            height: allocationBandHeight - (isDenseBandMode ? 4 : 8),
                            justifyContent:
                                showLabel && showBadge ? 'space-between' : isDenseBandMode ? 'flex-start' : 'center',
                            left: segmentLeftPx,
                            maxWidth: segmentWidthPx,
                            overflow: 'hidden',
                            padding: showLabel || showBadge ? `0 ${String(horizontalPaddingPx)}px` : '0',
                            pointerEvents: 'auto',
                            position: 'absolute',
                            textAlign: 'left',
                            top: isDenseBandMode ? 2 : 4,
                            width: segmentWidthPx,
                        }}
                        title={segment.title ?? segment.label}
                        type="button"
                    >
                        <span
                            style={{
                                alignItems: 'center',
                                display: 'flex',
                                gap: 6,
                                maxWidth: '100%',
                                minWidth: 0,
                                transform: contentOffsetPx > 0 ? `translateX(${String(contentOffsetPx)}px)` : undefined,
                            }}
                        >
                            {showLabel ? (
                                <span
                                    style={{
                                        fontSize: isDenseBandMode ? 10 : 11,
                                        fontWeight: 700,
                                        minWidth: 0,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {resolvedLabel}
                                </span>
                            ) : null}

                            {showBadge && segment.badgeLabel ? (
                                <span
                                    style={{
                                        backgroundColor: 'rgba(3, 14, 20, 0.28)',
                                        borderRadius: 999,
                                        flexShrink: 0,
                                        fontSize: 10,
                                        padding: '1px 6px',
                                    }}
                                >
                                    {segment.badgeLabel}
                                </span>
                            ) : null}
                        </span>
                    </button>
                );
            })}
        </div>
    );
}

export default memo(AllocationOverlayBand);
